import useProjectStore from "../components/ProjectCanvas/useProjectStore";
import { loadComponentDetail } from "./canvasApi";
import {
  canvasPerfElapsedMs,
  canvasPerfNow,
  logCanvasPerf,
} from "./canvasPerf";
import { hydrateComponentStoreFromComponent } from "./prefillComponentStores";

const inflightLoads = new Map();

function componentKey(projectId, version, componentId, published) {
  const mode = published ? "published" : "draft";
  return `${projectId || "unknown"}:${mode}:${version ?? "latest"}:${componentId}`;
}

function hasConfig(component) {
  if (component?.isCanvasViewShell) return false;
  return !!(component?.rawComponentData || component?.componentData);
}

function normalizeDetail(detail, fallbackType) {
  if (!detail) return null;
  return {
    ...detail,
    type: detail.type || fallbackType,
    rawComponentData: detail.rawComponentData || detail.componentData,
    componentData: detail.componentData || detail.rawComponentData,
  };
}

function mergeComponent(component) {
  if (!component?.componentId) return;
  const components = useProjectStore.getState().components || [];
  const idx = components.findIndex(
    (item) => item.componentId === component.componentId,
  );
  const nextComponents = [...components];

  if (idx >= 0) {
    nextComponents[idx] = {
      ...nextComponents[idx],
      ...component,
      isCanvasViewShell: false,
      rawComponentData:
        component.rawComponentData ?? nextComponents[idx].rawComponentData,
      componentData:
        component.componentData ?? nextComponents[idx].componentData,
    };
  } else {
    nextComponents.push(component);
  }

  useProjectStore.setState({ components: nextComponents });
}

export async function loadComponentConfig(
  componentId,
  {
    projectId,
    version = null,
    componentType = null,
    source = "unknown",
    published = false,
  } = {},
) {
  const existing = (useProjectStore.getState().components || []).find(
    (component) => component.componentId === componentId,
  );

  if (hasConfig(existing)) {
    logCanvasPerf("component-config-cache-hit", {
      projectId,
      componentId,
      componentType,
      source,
    });
    hydrateComponentStoreFromComponent(existing, version, componentType, {
      projectId,
      loadedFrom: existing.rawComponentData
        ? "FullCanvasProject"
        : "ComponentDetailDto",
      source,
    });
    return existing;
  }

  if (!projectId) {
    throw new Error(`Cannot load component ${componentId}: missing projectId`);
  }

  const key = componentKey(projectId, version, componentId, published);
  if (inflightLoads.has(key)) {
    logCanvasPerf("component-config-inflight-hit", {
      projectId,
      componentId,
      componentType,
      source,
    });
    return inflightLoads.get(key);
  }

  const startedAt = canvasPerfNow();
  const promise = loadComponentDetail(
    projectId,
    componentId,
    version,
    published,
  )
    .then((response) => {
      const component = normalizeDetail(response?.data, componentType);
      if (!component) {
        throw new Error(`Component ${componentId} was not found`);
      }

      mergeComponent(component);
      hydrateComponentStoreFromComponent(component, version, componentType, {
        projectId,
        loadedFrom: "ComponentDetailDto",
        source,
      });
      logCanvasPerf("component-config-loaded", {
        projectId,
        componentId,
        componentType: component.type || componentType,
        source,
        durationMs: canvasPerfElapsedMs(startedAt),
      });
      return component;
    })
    .finally(() => {
      inflightLoads.delete(key);
    });

  inflightLoads.set(key, promise);
  return promise;
}

export function clearComponentConfigLoadCache() {
  inflightLoads.clear();
}
